<template>
  <main v-if="route" class="wrapper" role="main">
    <MemberSessionModal
      :name="memberSessionModalName"
      :routeOnConfirm="memberSessionRedirectUrl"
    />
    <sc-text v-if="isEditMode" class="title" tag="h1" :field="title" />
    <h1 v-else-if="showTitle" :class="['title', { dark: isDarkTheme }]" >{{ title }}</h1>
    <placeholder
      name="digital-house-main"
      :rendering="route"
      :errorComponent="getErrorComponent"
    />
  </main>
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex'
import { Placeholder, Text } from '@sitecore-jss/sitecore-jss-vue'
import { v4 as uuid } from 'uuid'
import querystring from 'querystring'

import get from '~/helpers/get'
import UIMixin from '~/mixins/ui'
import ThemeMixin from '~/mixins/theme'
import NavBar from '~/components/nav-bar/NavBar'
import HouseBoard from '~/components/house-board'
import ProfileMenu from '~/components/profile-menu'
import isEmptyObject from '~/helpers/is-empty-object'
import { templateId, MEMBER_SESSION_SHOWN } from '~/constants/sitecore-keys'
import MemberSessionModal, { MEMBER_SESSION_MODAL } from '~/components/member-session-modal'
import ErrorComponent from '~/components/sitecore/error-component'
import Theme from '~/constants/theme'
import { ALL_TIERS } from '~/constants/permissions-tiers'

const { mapGetters: mapJssGetters, mapActions: mapJssActions, mapState: mapJssState } = createNamespacedHelpers('jss')
const { mapGetters: mapProfileGetters } = createNamespacedHelpers('user/profile')
const { mapGetters: mapEnvGetters, mapActions: mapEnvActions } = createNamespacedHelpers('ui/environment')

const NOTES_TEMPLATES = ['House Notes Landing Page', 'House Note Page', 'Editorial Story Page']

export default {
  name: 'home',

  allowedPermissionsTiers: ALL_TIERS,

  layout: 'jss-default',

  components: {
    Placeholder,
    NavBar,
    HouseBoard,
    ProfileMenu,
    ScText: Text,
    MemberSessionModal,
    ErrorComponent
  },

  mixins: [UIMixin, ThemeMixin],

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  async fetch ({ app, route, error, store, req, redirect }) {
    if (process.server) {
      // Remove any uppercase letters (expect from querystring)
      const match = req.url.match(/^([^?]*[A-Z][^?]*)(\?.*)?/)
      if (match) {
        const [, path, query] = match
        redirect(path.toLowerCase(), query && querystring.parse(query.replace('?', '')))
      }
    }

    if (store.state.jss.isExperienceEditor) {
      if (process.server) {
        store.commit('jss/setLayoutResponse', req.sitecoreData)
      }
    } else {
      await store.dispatch('jss/fetchRouteData', route)
    }

    const { statusCode } = store.state.jss

    const theme = store.getters['jss/colorTheme'] === Theme.Black ? Theme.Dark : Theme.Light
    store.dispatch('ui/environment/setThemeColor', theme)

    if ([404, 403].includes(statusCode)) {
      error({ statusCode })
    } else if (statusCode === 401) {
      app.$login(route.fullPath)
    }
  },

  beforeRouteLeave (to, from, next) {
    this.setThemeColor(Theme.Light)
    next()
  },

  head () {
    const canonical = this.canonicalUrl ? { rel: 'canonical', href: `${this.host}${this.canonicalUrl}` } : {}

    return {
      title: this.meta.title,
      link: [
        ...this.hrefLangs,
        canonical
      ],
      meta: [
        { hid: 'description', name: 'description', content: this.meta.description }
      ]
    }
  },

  data () {
    return {
      memberSessionRedirectUrl: '',
      host: ''
    }
  },

  async asyncData ({ req }) {
    return {
      host: process.server ? req.headers.host : window.location.host
    }
  },

  computed: {
    ...mapGetters(['currentUser']),
    ...mapJssState(['context', 'route']),
    ...mapJssGetters(['isEditMode']),
    ...mapEnvGetters(['isWebview']),
    ...mapProfileGetters(['profile']),

    getErrorComponent () {
      return ErrorComponent
    },

    hasProfile () {
      return this.profile && !isEmptyObject(this.profile)
    },

    hasRouteData () {
      return !!this.route
    },

    hrefLangs () {
      const links = (this.context && this.context.links) || []

      return links.map(item => (
        {
          ...item,
          // override nuxt alternate hreflang generation
          hid: `alternate-hreflang-${item.hrefLang}`,
          href: `${this.host}${item.href}`
        }
      ))
    },

    title () {
      return get(this.route, this.isEditMode ? 'fields.title' : 'fields.title.value', '')
    },

    showTitle () {
      // todo: The Page Title should be moved to the separate Sitecore component
      const isNotesTemplate = NOTES_TEMPLATES.includes(this.route.templateName)
      const hiddenTitle = get(this.route, 'fields.hideTitle.value', false)

      return !hiddenTitle && !isNotesTemplate && this.title
    },

    meta () {
      if (!this.hasRouteData) {
        return {
          title: this.$t('PAGE_HAS_NOT_FOUND'),
          description: this.$t('PAGE_HAS_NOT_FOUND')
        }
      }

      return {
        title: get(this.route, 'fields.metaTitle.value'),
        description: get(this.route, 'fields.metaDescription.value')
      }
    },

    memberSessionModalName () {
      return `${MEMBER_SESSION_MODAL}-${uuid()}`
    }
  },

  watch: {
    'route.templateId': {
      handler: 'openMemberSessionModal'
    }
  },

  mounted () {
    if (this.hasRouteData && this.route.templateId === templateId.bedrooms) {
      this.openMemberSessionModal(this.route.templateId)
    }
  },

  methods: {
    ...mapJssActions(['fetchRouteData']),
    ...mapEnvActions(['setThemeColor']),

    openMemberSessionModal (val) {
      if (this.$isServer
        || this.currentUser
        || sessionStorage.getItem(MEMBER_SESSION_SHOWN) === 'true'
        || val !== templateId.bedrooms
      ) {
        return
      }

      this.memberSessionRedirectUrl = this.localePath(this.$route)
      this.$ui.modal.open(this.memberSessionModalName)
    }
  }
}
</script>

<style lang="scss" scoped>
.wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  &:focus {
    outline: none;
  }

  .footer {
    margin-top: auto;
  }

  .title {
    @include font('090');

    padding: 0 $space-050 0;
    margin-top: $space-090;

    @include tablet {
      @include font('100');
      padding: 0 $space-060 $space-040;
      margin-top: $space-110;
    }
  }
}

.title {
  background-color: $color-white;

  &.dark {
    background-color: $color-black;
  }
}
</style>
